/**
 * KioskUI — shared brand components for the HKZ kiosk redesign.
 * All colors/fonts via CSS variables in src/index.css (from brand.css).
 */
import {
  FiGrid, FiTag, FiList, FiShoppingCart, FiArchive, FiSliders,
  FiUsers, FiTruck, FiCreditCard, FiFileText, FiSearch, FiPlus,
  FiPackage, FiAlertTriangle, FiClock, FiLogOut, FiLock, FiUnlock,
  FiChevronLeft, FiChevronRight, FiArrowLeft, FiDownload, FiCheck,
  FiX, FiTrash2, FiEdit2, FiDollarSign, FiCalendar, FiArrowUp,
  FiArrowDown, FiPrinter, FiMinusCircle,
} from "react-icons/fi";

const ICON_MAP = {
  overview: FiGrid, products: FiTag, categories: FiList,
  sale: FiShoppingCart, restock: FiArchive, adjust: FiSliders,
  customers: FiUsers, suppliers: FiTruck, payments: FiCreditCard,
  invoice: FiFileText, search: FiSearch, plus: FiPlus,
  box: FiPackage, alert: FiAlertTriangle, clock: FiClock,
  logout: FiLogOut, lock: FiLock, unlock: FiUnlock,
  chevLeft: FiChevronLeft, chevR: FiChevronRight, back: FiArrowLeft,
  download: FiDownload, check: FiCheck, x: FiX,
  trash: FiTrash2, edit: FiEdit2, cash: FiDollarSign,
  calendar: FiCalendar, arrowUp: FiArrowUp, arrowDown: FiArrowDown,
  print: FiPrinter, minus: FiMinusCircle,
};

export function Icon({ name, size = 18, stroke, style }) {
  const Comp = ICON_MAP[name];
  if (!Comp) return null;
  return <Comp size={size} style={{ color: stroke || "currentColor", flexShrink: 0, display: "block", ...style }} />;
}

/* ── Btn ─────────────────────────────────────────────────── */
const BTN_KINDS = {
  pri:    { background: "var(--primary)",    color: "#fff",            border: "transparent" },
  acc:    { background: "var(--accent)",     color: "#fff",            border: "transparent" },
  soft:   { background: "var(--blue-50)",    color: "var(--primary)",  border: "transparent" },
  ghost:  { background: "var(--surface)",    color: "var(--ink)",      border: "var(--line)" },
  danger: { background: "var(--danger-bg)",  color: "var(--danger)",   border: "transparent" },
  quiet:  { background: "transparent",       color: "var(--sub)",      border: "transparent" },
};

export function Btn({ kind = "pri", icon, sm, onClick, disabled, style, children }) {
  const k = BTN_KINDS[kind] || BTN_KINDS.ghost;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        fontFamily: "var(--font-head)", fontWeight: 700,
        fontSize: sm ? 13.5 : 14.5, letterSpacing: "-.01em",
        borderRadius: "var(--r-sm)",
        padding: sm ? "8px 13px" : "10px 17px",
        border: `1px solid ${k.border}`,
        background: k.background, color: k.color,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.45 : 1,
        display: "inline-flex", alignItems: "center", gap: 7,
        whiteSpace: "nowrap", transition: "filter .12s",
        ...style,
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.filter = "brightness(.93)"; }}
      onMouseLeave={e => { e.currentTarget.style.filter = "none"; }}
    >
      {icon && <Icon name={icon} size={sm ? 15 : 17} />}
      {children}
    </button>
  );
}

/* ── Pill ────────────────────────────────────────────────── */
const PILL_TONES = {
  ok:     { bg: "var(--success-bg)",  fg: "var(--success)" },
  in:     { bg: "var(--success-bg)",  fg: "var(--success)" },
  cash:   { bg: "var(--success-bg)",  fg: "var(--success)" },
  paid:   { bg: "var(--success-bg)",  fg: "var(--success)" },
  low:    { bg: "var(--warning-bg)",  fg: "var(--warning)" },
  credit: { bg: "var(--warning-bg)",  fg: "var(--warning)" },
  open:   { bg: "var(--warning-bg)",  fg: "var(--warning)" },
  out:    { bg: "var(--danger-bg)",   fg: "var(--danger)"  },
  info:   { bg: "var(--blue-50)",     fg: "var(--primary)" },
  muted:  { bg: "var(--n-100)",       fg: "var(--n-500)"   },
};

export function Pill({ tone, children }) {
  const t = PILL_TONES[tone] || PILL_TONES.muted;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: t.bg, color: t.fg,
      borderRadius: 999, padding: "4px 11px",
      fontSize: 12.5, fontWeight: 700,
      fontFamily: "var(--font-body)", whiteSpace: "nowrap",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, background: t.fg, flexShrink: 0 }} />
      {children}
    </span>
  );
}

/* ── Card ────────────────────────────────────────────────── */
export function Card({ children, style, onClick, ...rest }) {
  return (
    <div
      onClick={onClick}
      {...rest}
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-lg)",
        boxShadow: "var(--shadow-sm)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ── StatCard ────────────────────────────────────────────── */
const STAT_TONES = {
  blue:  { bg: "var(--blue-50)",     fg: "var(--primary)"  },
  amber: { bg: "var(--warning-bg)",  fg: "var(--warning)"  },
  green: { bg: "var(--success-bg)",  fg: "var(--success)"  },
  slate: { bg: "var(--n-100)",       fg: "var(--n-600)"    },
};

export function StatCard({ label, value, unit, icon, tone = "blue", sub }) {
  const t = STAT_TONES[tone] || STAT_TONES.blue;
  return (
    <Card style={{ padding: "18px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ fontSize: 13.5, color: "var(--sub)", fontWeight: 500 }}>{label}</div>
        <div style={{ width: 38, height: 38, borderRadius: 11, background: t.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon name={icon} size={19} stroke={t.fg} />
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 14 }}>
        <div style={{ fontFamily: "var(--font-head)", fontSize: 32, fontWeight: 800, letterSpacing: "-.02em", color: "var(--ink)", fontVariantNumeric: "tabular-nums" }}>
          {value}
        </div>
        {unit && <div style={{ fontSize: 13, fontWeight: 600, color: "var(--sub)" }}>{unit}</div>}
      </div>
      {sub && <div style={{ fontSize: 12.5, color: "var(--sub)", marginTop: 5 }}>{sub}</div>}
    </Card>
  );
}

/* ── PageHeader ──────────────────────────────────────────── */
export function PageHeader({ title, mm, desc, children }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20, marginBottom: 22, flexWrap: "wrap" }}>
      <div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 11 }}>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 27, fontWeight: 800, letterSpacing: "-.025em", color: "var(--ink)", margin: 0, whiteSpace: "nowrap" }}>
            {title}
          </h1>
          {mm && <span style={{ fontFamily: "var(--font-mm)", fontSize: 16, color: "var(--n-400)", fontWeight: 500 }}>{mm}</span>}
        </div>
        {desc && <div style={{ fontSize: 14, color: "var(--sub)", marginTop: 6 }}>{desc}</div>}
      </div>
      {children && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>{children}</div>
      )}
    </div>
  );
}

/* ── Table helpers ───────────────────────────────────────── */
export function THead({ cols }) {
  return (
    <thead>
      <tr>
        {cols.map((c, i) => (
          <th key={i} style={{
            textAlign: c.r ? "right" : "left",
            padding: "13px 16px",
            fontFamily: "var(--font-body)",
            fontSize: 12, fontWeight: 700,
            letterSpacing: ".04em", textTransform: "uppercase",
            color: "var(--sub)", whiteSpace: "nowrap",
            width: c.w,
          }}>
            {c.t}
          </th>
        ))}
      </tr>
    </thead>
  );
}

export function TD({ children, r, b, mut, style }) {
  return (
    <td style={{
      padding: "14px 16px",
      fontFamily: "var(--font-body)", fontSize: 14,
      color: mut ? "var(--sub)" : "var(--ink)",
      fontWeight: b ? 700 : 400,
      textAlign: r ? "right" : "left",
      borderTop: "1px solid var(--hair)",
      whiteSpace: "nowrap",
      fontVariantNumeric: r ? "tabular-nums" : "normal",
      ...style,
    }}>
      {children}
    </td>
  );
}

/* ── Pagination ─────────────────────────────────────────── */
const PER_PAGE = 20;

function PagBtn({ children, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        minWidth: 34, height: 34, padding: "0 8px",
        borderRadius: "var(--r-sm)",
        border: `1px solid ${active ? "var(--primary)" : disabled ? "var(--hair)" : "var(--line)"}`,
        background: active ? "var(--primary)" : "var(--surface)",
        color: active ? "#fff" : disabled ? "var(--n-300)" : "var(--ink)",
        cursor: disabled ? "default" : "pointer",
        fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 600,
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "background .1s",
      }}
    >{children}</button>
  );
}

export function Pagination({ total, page, perPage = PER_PAGE, onChange }) {
  const pages = Math.ceil(total / perPage);
  if (pages <= 1) return null;

  const start = (page - 1) * perPage + 1;
  const end   = Math.min(page * perPage, total);

  const nums = (() => {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const result = [];
    for (let i = 1; i <= pages; i++) {
      if (i === 1 || i === pages || Math.abs(i - page) <= 1) {
        result.push(i);
      } else if (result[result.length - 1] !== "…") {
        result.push("…");
      }
    }
    return result;
  })();

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: "1px solid var(--hair)", background: "var(--n-25)" }}>
      <span style={{ fontSize: 13, color: "var(--sub)" }}>
        {start}–{end} of {total}
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <PagBtn onClick={() => onChange(page - 1)} disabled={page === 1}>←</PagBtn>
        {nums.map((n, i) =>
          n === "…"
            ? <span key={`e${i}`} style={{ padding: "0 4px", fontSize: 13, color: "var(--sub)" }}>…</span>
            : <PagBtn key={n} onClick={() => onChange(n)} active={n === page}>{n}</PagBtn>
        )}
        <PagBtn onClick={() => onChange(page + 1)} disabled={page === pages}>→</PagBtn>
      </div>
    </div>
  );
}

export const PER_PAGE_DEFAULT = PER_PAGE;

/* ── Number formatters ───────────────────────────────────── */
export const fmtInvNo = (id) => `INV-${String(id).padStart(5, "0")}`;

export const fmtKs = (n) =>
  Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });

export const fmtRevenue = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(Math.round(n));
};
